/**
 * Fly-to-bag: a ghost of the product arcs from wherever the add happened into
 * the cart / wishlist icon in the top bar, and the icon kicks when it lands.
 *
 * Framework-free on purpose — contexts, OrbieeFlow and the agent helpers all
 * call it, and none of them hold a ref to the top bar. The top bar marks its
 * buttons with `data-fly-target="cart" | "wishlist"` and listens for the
 * `orbiee:fly-land` window event to play its kick.
 *
 * Where a flight starts:
 *   "pointer" (default) — the largest product image near the shopper's last
 *                         click, so the ghost leaves the card / hero / row
 *                         they actually pressed.
 *   "agent"             — the open chat panel, else the avatar widget, else
 *                         the bottom-right corner where the assistant lives.
 *   an Element          — that element (or the image nearest to it).
 */

export const FLY_LAND_EVENT = "orbiee:fly-land";

const DURATION_MS = 720;
// Two calls for the same product this close together are one gesture seen
// twice (a double-applied agent snapshot, a double click) — fly once.
const DEDUPE_MS = 450;
const POINTER_FRESH_MS = 5000;
const MIN_IMG_PX = 24;

const inFlight = { cart: 0, wishlist: 0 };
const lastLandAt = { cart: 0, wishlist: 0 };
const lastLaunchAt = new Map();

let lastPointer = null;
if (typeof document !== "undefined") {
  // Capture phase, so it is recorded before any handler that re-renders or
  // unmounts the element that was pressed.
  document.addEventListener(
    "pointerdown",
    (e) => {
      lastPointer = {
        el: e.target instanceof Element ? e.target : null,
        at: Date.now(),
      };
    },
    true
  );
}

/**
 * True while a ghost is travelling to `kind`, or has just landed. The top bar
 * uses it to skip its own count-change kick, so an add animates once.
 */
export function isFlightActive(kind, windowMs = 1200) {
  return (inFlight[kind] || 0) > 0 || Date.now() - (lastLandAt[kind] || 0) < windowMs;
}

/** Best product image URL from any shape the app or the agent uses. */
export function productImageOf(p) {
  if (!p || typeof p !== "object") return null;
  const first = Array.isArray(p.images) ? p.images[0] : null;
  const candidates = [
    p.image,
    p.image_uri,
    p.image_url,
    p.img,
    p.thumbnail,
    p.thumbnail_url,
    typeof first === "string" ? first : first?.url,
  ];
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return null;
}

const toRect = (r) => ({ left: r.left, top: r.top, width: r.width, height: r.height });

/** The element's box if it is actually painted inside the viewport. */
function visibleRect(el) {
  if (!el || !el.isConnected || typeof el.getBoundingClientRect !== "function") {
    return null;
  }
  const r = el.getBoundingClientRect();
  if (r.width < 2 || r.height < 2) return null;
  if (
    r.bottom <= 0 ||
    r.right <= 0 ||
    r.top >= window.innerHeight ||
    r.left >= window.innerWidth
  ) {
    return null;
  }
  return toRect(r);
}

/**
 * The biggest visible product image in the nearest ancestor that has one —
 * walks out from the button until it finds the card, row or gallery it sits in.
 */
function imageRectNear(el) {
  if (!el) return null;
  if (el.tagName === "IMG") {
    const own = visibleRect(el);
    if (own && own.width >= MIN_IMG_PX) return own;
  }
  let node = el;
  for (let depth = 0; node && node !== document.body && depth < 12; depth += 1) {
    // Never borrow the logo or avatar from the bar the ghost is flying to.
    if (node.classList?.contains("ob-topbar")) break;
    let best = null;
    let bestArea = 0;
    node.querySelectorAll("img").forEach((img) => {
      if (img.closest(".ob-topbar")) return;
      const r = visibleRect(img);
      if (!r || r.width < MIN_IMG_PX || r.height < MIN_IMG_PX) return;
      const area = r.width * r.height;
      if (area > bestArea) {
        best = r;
        bestArea = area;
      }
    });
    if (best) return best;
    node = node.parentElement;
  }
  return visibleRect(el);
}

function pointerSource() {
  if (!lastPointer || Date.now() - lastPointer.at > POINTER_FRESH_MS) return null;
  return imageRectNear(lastPointer.el);
}

function agentSource() {
  const panel = visibleRect(document.querySelector(".ob-chatpanel.ob-open"));
  if (panel) {
    // Leave from the conversation, low in the panel where the reply lands.
    const w = 72;
    const h = 96;
    return {
      left: panel.left + panel.width / 2 - w / 2,
      top: Math.max(panel.top, panel.top + panel.height - h - 120),
      width: w,
      height: h,
    };
  }
  const avatar = visibleRect(document.querySelector("[data-fly-agent]"));
  if (avatar) return avatar;
  return {
    left: window.innerWidth - 110,
    top: window.innerHeight - 140,
    width: 80,
    height: 104,
  };
}

function viewportCentre() {
  return {
    left: window.innerWidth / 2 - 50,
    top: window.innerHeight / 2 - 65,
    width: 100,
    height: 130,
  };
}

function land(kind) {
  lastLandAt[kind] = Date.now();
  try {
    window.dispatchEvent(new CustomEvent(FLY_LAND_EVENT, { detail: { kind } }));
  } catch {
    /* CustomEvent unsupported — the badge still updates, just without the kick */
  }
}

const prefersReducedMotion = () =>
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Launch a ghost into the cart or wishlist icon.
 *
 * @param {"cart" | "wishlist"} kind
 * @param {{ image?: string | null, from?: "pointer" | "agent" | Element, key?: string }} [opts]
 */
export function flyToBag(kind, opts = {}) {
  if (typeof window === "undefined" || typeof document === "undefined" || !document.body) {
    return;
  }
  if (kind !== "cart" && kind !== "wishlist") return;

  const { image = null, from = "pointer", key = "" } = opts;

  const dedupeKey = `${kind}:${key}`;
  const now = Date.now();
  if (now - (lastLaunchAt.get(dedupeKey) || 0) < DEDUPE_MS) return;
  lastLaunchAt.set(dedupeKey, now);
  if (lastLaunchAt.size > 50) lastLaunchAt.clear();

  const target = visibleRect(document.querySelector(`[data-fly-target="${kind}"]`));
  if (!target || prefersReducedMotion()) {
    land(kind);
    return;
  }

  let src = null;
  try {
    if (from === "agent") src = agentSource();
    else if (from instanceof Element) src = imageRectNear(from);
    else src = pointerSource();
  } catch {
    src = null;
  }
  if (!src) src = viewportCentre();

  const w = Math.max(40, Math.min(src.width, 110));
  const h = Math.max(52, Math.min(src.height, 140));
  const startX = src.left + src.width / 2;
  const startY = src.top + src.height / 2;
  const dx = target.left + target.width / 2 - startX;
  const dy = target.top + target.height / 2 - startY;

  const ghost = document.createElement("div");
  ghost.className = "ob-ghost";
  ghost.setAttribute("aria-hidden", "true");
  ghost.style.left = `${startX - w / 2}px`;
  ghost.style.top = `${startY - h / 2}px`;
  ghost.style.width = `${w}px`;
  ghost.style.height = `${h}px`;
  if (image) {
    // JSON.stringify quotes and escapes the URL for the url() token.
    ghost.style.backgroundImage = `url(${JSON.stringify(image)})`;
  } else {
    ghost.classList.add(kind === "wishlist" ? "ob-ghost-wish" : "ob-ghost-bag");
  }
  document.body.appendChild(ghost);

  inFlight[kind] += 1;
  let finished = false;
  let safety = null;
  const finish = () => {
    if (finished) return;
    finished = true;
    if (safety) clearTimeout(safety);
    ghost.remove();
    inFlight[kind] = Math.max(0, inFlight[kind] - 1);
    land(kind);
  };

  if (typeof ghost.animate !== "function") {
    finish();
    return;
  }

  const anim = ghost.animate(
    [
      { transform: "translate(0,0) scale(1) rotate(0deg)", opacity: 1, borderRadius: "14px" },
      {
        transform: `translate(${dx * 0.5}px, ${dy * 0.3 - 80}px) scale(.5) rotate(-8deg)`,
        opacity: 0.95,
        borderRadius: "40%",
        offset: 0.58,
      },
      {
        transform: `translate(${dx}px, ${dy}px) scale(.08) rotate(6deg)`,
        opacity: 0,
        borderRadius: "50%",
      },
    ],
    { duration: DURATION_MS, easing: "cubic-bezier(.45,.05,.7,.35)" }
  );
  anim.onfinish = finish;
  anim.oncancel = finish;
  // A backgrounded tab can hold an animation paused indefinitely; never leave
  // a ghost stuck on screen or the in-flight counter raised.
  safety = setTimeout(finish, DURATION_MS + 600);
}
